import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Container,
  IconButton,
  useMediaQuery,
  Chip,
  Radio,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  ArrowBack,
  PlayArrow,
  Description,
  ExpandMore,
  Launch,
  SportsEsports,
  Star,
  Assignment,
  Menu as MenuIcon,
  Close as CloseIcon,
  Visibility,
  CheckCircle,
} from '@mui/icons-material';
import { getCourseStructure } from '../services/api.js';

const getTipo = (aula) => {
  if (!aula) return 'video';
  if (aula.questionario === true) return 'questionario';
  if (aula.simulador === true) return 'simulador';
  if (aula.is_feedback === true) return 'feedback';
  if (aula.avaliacao === true) return 'avaliacao';
  if (aula.atividade === true && aula.atividades) return 'atividade_teorica';
  if (aula.atividade === true) return 'atividade';
  if (aula.video_info) return 'video';
  return aula.cd_tipo_conteudo || 'video';
};

const badgeConfig = {
  video: { label: 'Vídeo-aula', bgcolor: '#E8F5E9', color: '#2E7D32' },
  avaliacao: { label: 'Avaliação', bgcolor: '#FFF3E0', color: '#E65100' },
  atividade: { label: 'Atividade', bgcolor: '#E3F2FD', color: '#1565C0' },
  atividade_teorica: { label: 'Atividade', bgcolor: '#E3F2FD', color: '#1565C0' },
  simulador: { label: 'Simulador', bgcolor: '#F3E5F5', color: '#7B1FA2' },
  feedback: { label: 'Feedback', bgcolor: '#E0F7FA', color: '#00695C' },
  questionario: { label: 'Questionário', bgcolor: '#FFF8E1', color: '#F57F17' },
};

const StaffCourseViewerPage = () => {
  const { cursoVersaoId } = useParams();
  const navigate = useNavigate();
  const contentRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [courseData, setCourseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [moduloAtualIdx, setModuloAtualIdx] = useState(0);
  const [aulaAtualIdx, setAulaAtualIdx] = useState(0);
  const [expandedModulo, setExpandedModulo] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await getCourseStructure(cursoVersaoId);
        setCourseData(data);
        setExpandedModulo(0);
      } catch (err) {
        setError(err.message || 'Erro ao carregar estrutura do curso');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [cursoVersaoId]);

  const modulos = courseData?.modulos || [];
  const moduloAtual = modulos[moduloAtualIdx];
  const aulaAtual = moduloAtual?.conteudos?.[aulaAtualIdx];
  const currentTipo = getTipo(aulaAtual);

  const mudarAula = (mIdx, aIdx) => {
    setModuloAtualIdx(mIdx);
    setAulaAtualIdx(aIdx);
    setExpandedModulo(mIdx);
    if (isMobile) setSidebarOpen(false);
    if (contentRef.current) {
      contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getVideoSrc = (aula) => {
    let src = '';
    if (aula?.video_info?.embed_url) {
      const match = aula.video_info.embed_url.match(/src=["']([^"']+)["']/);
      src = match ? match[1] : aula.video_info.embed_url;
    } else if (aula?.link) {
      src = aula.link.replace('vimeo.com', 'player.vimeo.com/video');
    }
    if (src) {
      const separator = src.includes('?') ? '&' : '?';
      src += `${separator}api=1&player_id=vimeo-player-staff`;
    }
    return src;
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: '#f8fafc',
          minHeight: '100vh',
        }}
      >
        <CircularProgress color='primary' />
      </Box>
    );
  }

  if (error || !courseData) {
    return (
      <Container maxWidth='sm' sx={{ mt: 8 }}>
        <Alert severity='error'>{error || 'Curso não encontrado'}</Alert>
        <Button
          fullWidth
          variant='contained'
          startIcon={<ArrowBack />}
          onClick={() => window.history.state?.idx > 0 ? navigate(-1) : navigate('/home')}
          sx={{ mt: 2 }}
        >
          Voltar
        </Button>
      </Container>
    );
  }

  const renderContentArea = () => {
    if (!aulaAtual) {
      return (
        <Box sx={{ textAlign: 'center', py: 10, color: 'text.secondary' }}>
          <Visibility sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
          <Typography variant='h6'>Selecione um conteúdo na barra lateral</Typography>
        </Box>
      );
    }

    return (
      <>
        <Typography variant='caption' fontWeight='bold' color='primary' gutterBottom>
          {moduloAtual.titulo}
        </Typography>
        <Typography variant='h4' fontWeight='bold' sx={{ mb: 1 }}>
          {aulaAtual.video_info?.title || aulaAtual.titulo}
        </Typography>

        {/* Description */}
        {aulaAtual.descricao && (
          <Box
            sx={{
              mb: 3,
              p: 2,
              bgcolor: '#f0f4ff',
              borderRadius: 2,
              border: '1px solid #d0dcf0',
            }}
          >
            <Typography variant='body1' color='text.secondary'>
              {aulaAtual.descricao}
            </Typography>
          </Box>
        )}

        {/* Video */}
        {currentTipo === 'video' && (
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              maxWidth: '1200px',
              mx: 'auto',
              aspectRatio: '16/9',
              bgcolor: '#000',
              borderRadius: '12px',
              overflow: 'hidden',
              boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
              mb: 4,
            }}
          >
            <iframe
              id='vimeo-player-staff'
              src={getVideoSrc(aulaAtual)}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                border: 'none',
              }}
              frameBorder='0'
              allow='autoplay; fullscreen; picture-in-picture'
              allowFullScreen
              title={aulaAtual.titulo}
            />
          </Box>
        )}

        {/* Activity */}
        {currentTipo === 'atividade' && (
          <Box
            sx={{
              mb: 4,
              p: 3,
              bgcolor: '#f8fafc',
              borderRadius: '12px',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Assignment color='primary' sx={{ mr: 1 }} />
              <Typography variant='h6' fontWeight='bold'>
                Atividade Prática
              </Typography>
            </Box>
            <Typography variant='body1' sx={{ mb: 3 }}>
              {aulaAtual.descricao || 'Esta atividade requer o uso do Gêmeo Digital para prática.'}
            </Typography>
            {aulaAtual.link && (
              <Button
                variant='contained'
                color='primary'
                startIcon={<Launch />}
                href={aulaAtual.link}
                target='_blank'
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: 'bold',
                }}
              >
                Abrir Gêmeo Digital
              </Button>
            )}
          </Box>
        )}

        {/* Simulator */}
        {currentTipo === 'simulador' && (
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              maxWidth: '1200px',
              mx: 'auto',
              aspectRatio: '16/9',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              bgcolor: '#f8fafc',
              borderRadius: '12px',
              border: '1px solid',
              borderColor: 'divider',
              p: 4,
              textAlign: 'center',
              mb: 4,
            }}
          >
            <SportsEsports sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
            <Typography variant='h5' fontWeight='bold' gutterBottom>
              Simulador Prático
            </Typography>
            <Typography variant='body1' color='text.secondary' sx={{ mb: 4, maxWidth: 600 }}>
              Acesse o simulador interativo para realizar a atividade prática.
            </Typography>
            {aulaAtual.link && (
              <Button
                variant='contained'
                size='large'
                startIcon={<Launch />}
                href={aulaAtual.link}
                target='_blank'
                sx={{
                  px: 5,
                  py: 1.5,
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontWeight: 'bold',
                  fontSize: '1rem',
                }}
              >
                Abrir Simulador
              </Button>
            )}
          </Box>
        )}

        {/* Evaluation / Theoretical Activity - Read-only */}
        {(currentTipo === 'avaliacao' || currentTipo === 'atividade_teorica') && (
          <Box
            sx={{
              mb: 4,
              p: 3,
              bgcolor: '#f8fafc',
              borderRadius: '12px',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Assignment color='primary' sx={{ mr: 1, fontSize: 28 }} />
              <Typography variant='h5' fontWeight='bold'>
                {currentTipo === 'avaliacao' ? 'Avaliação' : 'Atividade Teórica'}
              </Typography>
            </Box>
            <Typography variant='body1' color='text.secondary'>
              {aulaAtual.descricao || 'Este conteúdo é uma avaliação/atividade disponível para os alunos matriculados.'}
            </Typography>
            <Chip
              label='Visualização somente — conteúdo disponível para alunos'
              sx={{ mt: 2, bgcolor: '#FFF3E0', color: '#E65100', fontWeight: 600 }}
            />
            
            {/* Render questions with right answers */}
            {aulaAtual.atividades && aulaAtual.atividades.length > 0 && (
              <Box sx={{ mt: 4 }}>
                <Typography variant='h6' fontWeight='bold' sx={{ mb: 3, borderBottom: '1px solid #e2e8f0', pb: 1 }}>
                  Questões e Respostas
                </Typography>
                {aulaAtual.atividades.map((questao, qIdx) => (
                  <Box key={questao.id_curso_avaliacao || qIdx} sx={{ mb: 4, p: 3, bgcolor: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                    <Typography variant='subtitle1' fontWeight='bold' sx={{ mb: 2 }}>
                      {qIdx + 1}. {questao.nm_questao}
                    </Typography>
                    
                    {questao.questoes && questao.questoes.length > 0 ? (
                      <List disablePadding>
                        {questao.questoes.map((alt, aIdx) => (
                          <ListItem 
                            key={alt.id_curso_avaliacao_questao || aIdx}
                            sx={{ 
                              mb: 1,
                              borderRadius: '8px',
                              bgcolor: alt.bo_ok ? '#f0fdf4' : 'transparent',
                              border: alt.bo_ok ? '1px solid #bbf7d0' : '1px solid #e2e8f0',
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 40 }}>
                              {alt.bo_ok ? <CheckCircle sx={{ color: '#22c55e' }} /> : <Radio disabled />}
                            </ListItemIcon>
                            <ListItemText 
                              primary={alt.nm_alternativa} 
                              secondary={alt.bo_ok && alt.nm_reposta_detalhe ? alt.nm_reposta_detalhe : null}
                              primaryTypographyProps={{ 
                                color: alt.bo_ok ? '#15803d' : 'text.primary',
                                fontWeight: alt.bo_ok ? 'bold' : 'normal'
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Typography variant='body2' color='text.secondary' sx={{ fontStyle: 'italic' }}>
                        Questão dissertativa. Resposta esperada ou critérios de correção não disponíveis no preview.
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* Feedback - Read-only */}
        {currentTipo === 'feedback' && (
          <Box
            sx={{
              mb: 4,
              p: 3,
              bgcolor: '#f8fafc',
              borderRadius: '12px',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Star color='primary' sx={{ mr: 1, fontSize: 28 }} />
              <Typography variant='h5' fontWeight='bold'>
                Feedback
              </Typography>
            </Box>
            <Typography variant='body1' color='text.secondary'>
              {aulaAtual.descricao || 'Questionário de feedback disponível para os alunos.'}
            </Typography>
            <Chip
              label='Visualização somente — conteúdo disponível para alunos'
              sx={{ mt: 2, bgcolor: '#FFF3E0', color: '#E65100', fontWeight: 600 }}
            />
          </Box>
        )}

        {/* Questionario - Read-only */}
        {currentTipo === 'questionario' && (
          <Box
            sx={{
              mb: 4,
              p: 3,
              bgcolor: '#f8fafc',
              borderRadius: '12px',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Assignment color='primary' sx={{ mr: 1, fontSize: 28 }} />
              <Typography variant='h5' fontWeight='bold'>
                Questionário
              </Typography>
            </Box>
            <Typography variant='body1' color='text.secondary'>
              {aulaAtual.descricao || 'Questionário disponível para os alunos matriculados.'}
            </Typography>
            <Chip
              label='Visualização somente — conteúdo disponível para alunos'
              sx={{ mt: 2, bgcolor: '#FFF3E0', color: '#E65100', fontWeight: 600 }}
            />
          </Box>
        )}

        {/* Auxiliary Materials */}
        {aulaAtual.materiais_auxiliares && aulaAtual.materiais_auxiliares.length > 0 && (
          <Box sx={{ mt: 4 }}>
            <Typography variant='h6' fontWeight='bold' sx={{ mb: 2 }}>
              Materiais Auxiliares
            </Typography>
            {aulaAtual.materiais_auxiliares.map((mat, idx) => (
              <Button
                key={idx}
                variant='outlined'
                startIcon={<Description />}
                href={mat.url || mat.link}
                target='_blank'
                sx={{
                  mr: 1,
                  mb: 1,
                  textTransform: 'none',
                  borderRadius: '8px',
                }}
              >
                {mat.titulo || mat.nome || `Material ${idx + 1}`}
              </Button>
            ))}
          </Box>
        )}
      </>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        minHeight: 'calc(100vh - 64px)',
        bgcolor: '#f8fafc',
        position: 'relative',
        pt: { xs: '56px', sm: '64px' },
      }}
    >
      {/* Mobile sidebar toggle */}
      {isMobile && (
        <IconButton
          onClick={() => setSidebarOpen(!sidebarOpen)}
          sx={{
            position: 'fixed',
            top: 72,
            left: 16,
            zIndex: 1300,
            bgcolor: 'white',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            '&:hover': { bgcolor: '#f5f5f5' },
          }}
        >
          {sidebarOpen ? <CloseIcon /> : <MenuIcon />}
        </IconButton>
      )}

      {/* Sidebar */}
      <Box
        sx={{
          width: { xs: '85vw', md: '25%' },
          minWidth: { md: 320 },
          maxWidth: { md: 420 },
          position: { xs: 'fixed', md: 'sticky' },
          top: { xs: 0, md: '64px' },
          bottom: 0,
          left: 0,
          bgcolor: '#FAFAFA',
          borderRight: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          zIndex: { xs: 1200, md: 10 },
          boxShadow: {
            xs: '4px 0 24px rgba(0,0,0,0.15)',
            md: '4px 0 12px rgba(0,0,0,0.03)',
          },
          transform: {
            xs: sidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
            md: 'translateX(0)',
          },
          transition: 'transform 0.3s ease-in-out',
          height: { xs: '100vh', md: 'calc(100vh - 64px)' },
        }}
      >
        <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => window.history.state?.idx > 0 ? navigate(-1) : navigate('/home')}
            sx={{ mb: 2, color: 'text.secondary', textTransform: 'none' }}
          >
            Voltar
          </Button>
          <Typography variant='h6' fontWeight='bold' noWrap gutterBottom>
            Conteúdo do Curso
          </Typography>
          <Chip
            icon={<Visibility sx={{ fontSize: 16 }} />}
            label='Modo Visualização'
            size='small'
            sx={{
              bgcolor: '#E3F2FD',
              color: '#1565C0',
              fontWeight: 700,
              '& .MuiChip-icon': { color: '#1565C0' },
            }}
          />
        </Box>

        <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto' }}>
          {modulos.map((mod, mIdx) => (
            <Accordion
              key={mod.id_curso_modulo}
              elevation={0}
              disableGutters
              expanded={expandedModulo === mIdx}
              onChange={(event, isExpanded) => {
                setExpandedModulo(isExpanded ? mIdx : null);
              }}
              sx={{
                '&:before': { display: 'none' },
                borderBottom: '1px solid',
                borderColor: '#f1f5f9',
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{
                  bgcolor: 'transparent',
                  '&.Mui-expanded': { bgcolor: '#F0F4F8' },
                  minHeight: 64,
                }}
              >
                <ListItemText
                  primary={`Módulo ${mod.ordem}`}
                  secondary={mod.titulo}
                  primaryTypographyProps={{
                    variant: 'caption',
                    fontWeight: 'bold',
                    color: 'primary',
                  }}
                  secondaryTypographyProps={{
                    variant: 'body2',
                    fontWeight: 'bold',
                    color: 'text.primary',
                    noWrap: true,
                  }}
                />
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0, pb: 1 }}>
                <List disablePadding>
                  {(mod.conteudos || []).map((aula, aIdx) => {
                    const isSelected = mIdx === moduloAtualIdx && aIdx === aulaAtualIdx;
                    const type = getTipo(aula);
                    const badge = badgeConfig[type] || { label: 'Material', bgcolor: '#F5F5F5', color: '#616161' };

                    const iconSx = {
                      color: isSelected ? 'primary.main' : '#94a3b8',
                      fontSize: 22,
                    };
                    let typeIcon;
                    if (type === 'video') {
                      typeIcon = <PlayArrow sx={iconSx} />;
                    } else if (type === 'avaliacao' || type === 'atividade' || type === 'atividade_teorica' || type === 'questionario') {
                      typeIcon = <Assignment sx={iconSx} />;
                    } else if (type === 'simulador') {
                      typeIcon = <SportsEsports sx={iconSx} />;
                    } else if (type === 'feedback') {
                      typeIcon = <Star sx={iconSx} />;
                    } else {
                      typeIcon = <Description sx={iconSx} />;
                    }

                    return (
                      <ListItem
                        key={aula.id_curso_conteudo}
                        button
                        onClick={() => mudarAula(mIdx, aIdx)}
                        sx={{
                          mx: 1.5,
                          mb: 0.5,
                          pl: 1.5,
                          pr: 1.5,
                          py: 1.5,
                          borderRadius: '12px',
                          bgcolor: isSelected ? '#FFFFFF' : 'transparent',
                          boxShadow: isSelected ? '0 4px 14px rgba(0,0,0,0.04)' : 'none',
                          border: isSelected ? '1px solid rgba(0,0,0,0.04)' : '1px solid transparent',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            bgcolor: !isSelected ? '#F0F4F8' : '#FFFFFF',
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          {typeIcon}
                        </ListItemIcon>

                        <Typography
                          variant='body2'
                          sx={{
                            fontWeight: 600,
                            color: isSelected ? 'primary.main' : '#94a3b8',
                            mr: 1,
                            flexShrink: 0,
                            fontSize: '0.85rem',
                          }}
                        >
                          {mod.ordem}.{aIdx + 1}
                        </Typography>

                        <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Typography
                            variant='body2'
                            sx={{
                              fontWeight: isSelected ? 700 : 500,
                              color: isSelected ? 'primary.main' : 'text.primary',
                              lineHeight: 1.4,
                            }}
                          >
                            {aula.titulo}
                          </Typography>
                          <Chip
                            label={badge.label}
                            size='small'
                            sx={{
                              height: 22,
                              fontSize: '0.65rem',
                              fontWeight: 700,
                              bgcolor: badge.bgcolor,
                              color: badge.color,
                              borderRadius: '6px',
                              alignSelf: 'flex-start',
                              '& .MuiChip-label': { px: 1 },
                            }}
                          />
                        </Box>
                      </ListItem>
                    );
                  })}
                </List>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Box>

      {/* Mobile sidebar overlay */}
      {isMobile && sidebarOpen && (
        <Box
          onClick={() => setSidebarOpen(false)}
          sx={{
            position: 'fixed',
            inset: 0,
            bgcolor: 'rgba(0,0,0,0.4)',
            zIndex: 1199,
          }}
        />
      )}

      {/* Main Content Area */}
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'white',
          minHeight: 'calc(100vh - 64px)',
        }}
      >
        <Box
          ref={contentRef}
          sx={{
            flexGrow: 1,
            p: { xs: 2, md: 4 },
            pb: { xs: 3, md: 4 },
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              maxWidth: { xs: '100%', lg: '85%', xl: '80%' },
              width: '100%',
            }}
          >
            {renderContentArea()}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default StaffCourseViewerPage;
